import { type AnyRouteMatch, type AnyRouter, useMatch, useRouter } from '@tanstack/react-router';
import * as React from 'react';

import type { LooseSearch } from './useSearch';

/**
 * `undefined` deletes a key; keys the patch leaves out stay as they are. A route whose chain declares no search has
 * the search `{}`, which takes any object, so its patch is `stringSearch({})`'s weak type, which rejects any key.
 */
export type SearchPatch<TSearch> = keyof TSearch extends never
    ? {
          /** Never set: the one key of a weak type, so an object with any other key fails to compile. */
          readonly __noSearchParams?: never;
      }
    : { [K in keyof TSearch]?: TSearch[K] | undefined };

export type SetSearchOptions = {
    /**
     * Adds a history entry instead of replacing the current one. Replacing is the default: a query param almost always
     * refines what is on screen (a filter, a tab, an open drawer), and Back should leave the page, not step through it.
     */
    push?: boolean;
    /** Skips the unsaved-changes guard, for state that isn't the user's work, such as a table's sorting. */
    ignoreBlocker?: boolean;
};

/** What `useSetSearch()` and `getRouteApi(id).useSetSearch()` return. */
export type SetSearch<TSearch> = (patch: SearchPatch<TSearch>, options?: SetSearchOptions) => void;

/**
 * The query-string writer of the route that renders the caller; the patch may name any key a route declares. A route
 * file passes it to its page as `onSearchChange`. In the app, lint allows this hook only in route files, apart from
 * the exceptions in documentation/routing-reference.md.
 *
 * - `setSearch` navigates, so call it from an event handler: lint rejects it during render and in an effect, but only
 *   in the file that calls this hook, not where a prop carries it.
 * - It merges the patch into the query string as it is when called, so two writes in one handler both land, also
 *   while the unsaved-changes guard asks about them; it asks once.
 * - Each write stays on the current page: it keeps the history state and the scroll position, and drops the hash.
 *   The pathname is rebuilt from the route, so a URL no link writes (`/Parts/…`) comes back canonical (`/parts/…`).
 * - Deleting a key the route requires renders `NotFoundPage`: navigate to another page instead.
 * - Readers get the new value once the router has loaded it, a few microtasks later: don't bind a controlled text
 *   input's `value` to the query string, keep the text in state and write it from there.
 * - A write from a page being left, while the router loads the next one, is dropped.
 */
export function useSetSearch(): SetSearch<LooseSearch> {
    return useSearchWriter();
}

/** The writer behind both forms of `useSetSearch`. */
export function useSearchWriter(): SetSearch<Record<string, unknown>> {
    const router = useRouter();
    // The route whose component renders the caller, or `undefined` outside every route's component.
    const routeId: string | undefined = useMatch({
        strict: false,
        shouldThrow: false,
        select: (match) => match.routeId,
    });

    return React.useCallback(
        (patch, { push = false, ignoreBlocker }: SetSearchOptions = {}) => {
            if (isLeavingRoute(router, routeId)) {
                return;
            }
            void router.navigate({
                to: '.',
                search: (search: Record<string, unknown>) => ({ ...search, ...patch }),
                replace: !push,
                resetScroll: false,
                state: true,
                ignoreBlocker,
            });
        },
        [router, routeId],
    );
}

/**
 * Whether the router is loading a location that doesn't keep this instance of `routeId` on screen: the route isn't
 * matched there, or it or a layout above it remounts (`remountDeps`). TanStack keeps the page being left mounted until
 * the next one is ready, and `to: '.'` resolves against the location being loaded, so a write from the page being
 * left would land on the next page's URL.
 */
function isLeavingRoute(router: AnyRouter, routeId: string | undefined): boolean {
    const { status, location, matches } = router.state;
    if (routeId === undefined || status !== 'pending') {
        return false;
    }
    const next = router.matchRoutes(location);
    const index = next.findIndex((match) => match.routeId === routeId);
    if (index === -1) {
        return true;
    }
    return next.slice(0, index + 1).some((nextMatch) => {
        const match = matches.find((candidate) => candidate.routeId === nextMatch.routeId);
        return match !== undefined && remountKey(router, match) !== remountKey(router, nextMatch);
    });
}

/**
 * The key TanStack renders a match's component under (`MatchInner` in `@tanstack/react-router`). TanStack passes
 * `remountDeps` the validated params and search; the public ones used here agree on every declared key.
 */
function remountKey(router: AnyRouter, match: AnyRouteMatch): string | undefined {
    const remountDeps = router.routesById[match.routeId].options.remountDeps ?? router.options.defaultRemountDeps;
    const deps: unknown = remountDeps?.({
        routeId: match.routeId,
        loaderDeps: match.loaderDeps,
        params: match.params,
        search: match.search,
    });
    return deps ? JSON.stringify(deps) : undefined;
}
